import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { PasswordInput } from "@/components/PasswordInput";
import { isAdminHost, publicBaseUrl } from "@/lib/host";

// Page de connexion dédiée à admin.gospot.ca — réservée aux comptes de rôle Administrateur.
// Utilise l'authentification existante (même endpoint, mêmes comptes) : aucun nouveau système d'auth.
export default function AdminLoginPage() {
  const { user, login, logout } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Cible après connexion : /panneau sur le sous-domaine admin, /admin sur le domaine principal
  const panelPath = isAdminHost() ? "/panneau" : "/admin";

  useEffect(() => {
    if (user && user.role === "admin") navigate(panelPath, { replace: true });
  }, [user, navigate, panelPath]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const account = await login(email, password);
      if (account.role !== "admin") {
        // Identifiants valides mais rôle insuffisant : on ferme la session aussitôt
        await logout();
        setError(t("Accès réservé aux administrateurs."));
        return;
      }
      toast.success(t("Bon retour sur GoSpot !"));
      navigate(panelPath);
    } catch (err) {
      setError(t(formatApiError(err.response?.data?.detail)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-[#F8FAFC] px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#2563EB] to-[#06B6D4] shadow-[0_12px_32px_rgba(37,99,235,0.3)]">
            <MapPin className="h-7 w-7 text-white" />
          </div>
          <span className="mx-auto mt-5 inline-flex items-center gap-1.5 rounded-full bg-[#0C1B33] px-3.5 py-1.5 text-[11px] font-bold text-white">
            <ShieldCheck className="h-3.5 w-3.5" />
            {t("Espace administrateur")}
          </span>
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t("Connexion à l'administration")}
          </h1>
          <p className="mt-2 text-sm text-slate-500">{t("Réservé à l'équipe GoSpot.")}</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-3 rounded-3xl border border-slate-100 bg-white p-6 shadow-[0_8px_32px_rgba(15,23,42,0.06)] sm:p-8">
          <input
            data-testid="admin-login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("Courriel")}
            required
            autoComplete="email"
            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[#2563EB] focus:ring-2 focus:ring-blue-100"
          />
          <PasswordInput
            testId="admin-login-password"
            toggleTestId="toggle-admin-password-visibility"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t("Mot de passe")}
            autoComplete="current-password"
          />
          {error && (
            <p data-testid="admin-login-error" className="rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loading}
            className="w-full rounded-full bg-[#2563EB] py-3.5 text-sm font-semibold text-white transition hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
          >
            {loading ? t("Connexion…") : t("Se connecter")}
          </button>
        </form>

        <p className="mt-6 text-center">
          <a
            href={isAdminHost() ? publicBaseUrl() : "/"}
            data-testid="admin-login-back-public"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition hover:text-[#2563EB]"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("Retour au site public")}
          </a>
        </p>
      </div>
    </main>
  );
}
